import { useState } from "react";
import {
  permissionConfig,
  PermissionLevel,
  PermissionTypes,
} from "shared";

import { CardContent } from "../../../../common/Atoms/CardContent";
import { FormDescription } from "../../../../common/Atoms/FormDescription";
import { InputTextAddon } from "../../../../common/Atoms/InputTextAddon";
import { Modal } from "../../../../common/Atoms/Modal";
import { Heading } from "../../../../common/Atoms/Typography/Heading";
import { useInviteUserMutation } from "../../../services/api/userApi/user";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { Button } from "../../../../common/Atoms/Button";
import { TwoColFormRow } from "../../Molecules/Form/TwoColFormRow";
import { Select } from "../../../../common/Atoms/Select";
import { settingPermissionOptions } from "../../../lib/helper";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { Divider } from "../../../../common/Atoms/Divider";

interface InviteModalProps {
  open: boolean;
  setOpen: () => void;
}

export function InviteModal({
  open,
  setOpen,
}: InviteModalProps) {
  const [inviteUser, { isLoading: inviteUserLoading }] = useInviteUserMutation();
  const [ inviteName, setInviteName ] = useState<string>(``);
  const [ inviteEmail, setInviteEmail ] = useState<string>(``);
  const [ settingPermission, setSettingPermission ] = useState(PermissionLevel.EDITOR);
  const currentOrg = useGetSelectedOrganisation();

  // check invite limits, 10 is the max enforced on the server
  const orgTooManyInvites = currentOrg?.pendingInvites?.length >= 10;

  async function onInvite() {
    await inviteUser({ inviteName, inviteEmail, orgId: currentOrg.id, settingPermission });
    setInviteEmail(``);
    setInviteName(``);

    // Close modal after inviting
    setOpen();
  }

  return (
    <Modal
      title={ `` }
      open={ open }
      setOpen={ setOpen }
      width={ `xl` }
    >
      <CardContent>
        <div
          className={ `space-y-3` }
        >
          <Heading>
            { `Invite a new user` }
          </Heading>
          <FormDescription>
            { `You can invite another user to join this Paidnice account` }
          </FormDescription>

          <div
            className={ `flex items-end justify-between w-full` }
          >
            <InputTextAddon
              className={ `w-1/2 mr-6` }
              label={ `Name` }
              value={ inviteName }
              onChange={ e => setInviteName(e.value as string) }
            />

            <InputTextAddon
              className={ `w-1/2` }
              label={ `Email` }
              value={ inviteEmail }
              onChange={ e => setInviteEmail(e.value as string) }
            />
          </div>

          <Divider
            weight={ `lighter` }
            spacing={ `small` }
          />

          <TwoColFormRow
            heading={ `Permissions` }
            description={ `Set the default permission levels for the new user` }
          >
            <Paragraph>
              { permissionConfig[PermissionTypes.SETTINGS].title }
            </Paragraph>
            <Paragraph
              variant={ `secondary` }
            >
              { permissionConfig[PermissionTypes.SETTINGS].description }
            </Paragraph>
            <Select
              selected={ settingPermission }
              options={ settingPermissionOptions }
              onChange={ e => setSettingPermission(e.value as PermissionLevel) }
            />
          </TwoColFormRow>
          <div className={ `flex justify-end mt-3` }>
            <Button
              onClick={ onInvite }
              type={ `button` }
              disabled={ orgTooManyInvites }
              loading={ inviteUserLoading }
            >
              { `Invite` }
            </Button>
          </div>
        </div>
      </CardContent>
    </Modal>
  );
}
